import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isObservable } from 'rxjs';
import { AbstractDialogComponent } from '../abstract-dialog-component.directive';
import { SolText } from '../../../models/core/sol-text';
import { SolDialogType } from '../../../models/core/sol-dialog';
import { SolValidationMessage } from '../../../models/core/sol-validation-message';
import { SolDialogComponent } from '../sol-dialog/sol-dialog.component';
import { SolAlertComponent } from '../../core/sol-alert/sol-alert.component';
import { SolLayoutComponent } from '../../core/sol-layout/sol-layout.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolConfirmDialogState } from './sol-confirm-dialog.state';

@Component({
  selector: 'sol-confirm-dialog',
  imports: [SolDialogComponent, SolAlertComponent, SolLayoutComponent, TranslatePipe],
  templateUrl: './sol-confirm-dialog.component.html',
})
export class SolConfirmDialogComponent extends AbstractDialogComponent<SolConfirmDialogState> {
  protected readonly dialog$ = this.compute(state => state.dialog);
  protected readonly text$ = this.compute(state => state.text);
  protected readonly messages$ = this.compute(state => state.messages);

  constructor() {
    super(SolConfirmDialogState.of());
  }

  public configure(title: SolText, text: SolText, confirmText?: SolText, type?: SolDialogType): void {
    this.update(s => s.withConfig(title, text, confirmText, type));
  }

  public showErrors(msgs: SolValidationMessage[]): void {
    this.update(s => s.withMessages(msgs).withDialog(s.dialog.stopLoading()));
  }

  protected handleConfirm(): void {
    this.update(s => s.withMessages([]).withDialog(s.dialog.startLoading()));
    const result = this.onConfirm();
    if (!isObservable(result)) {
      return;
    }
    result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.close(),
      error: (error: unknown) => this.showErrors(SolConfirmDialogComponent.toMessages(error)),
    });
  }

  private static toMessages(error: unknown): SolValidationMessage[] {
    if (Array.isArray(error)) {
      return error as SolValidationMessage[];
    }
    if (error instanceof SolText) {
      return [{ type: 'error', text: error }];
    }
    if (SolConfirmDialogComponent.hasValidationMessages(error)) {
      return error.messages;
    }
    return [{ type: 'error', text: SolText.of('sol-error-page.unknown.message1') }];
  }

  private static hasValidationMessages(error: unknown): error is { messages: SolValidationMessage[] } {
    return (
      typeof error === 'object' &&
      error !== null &&
      'messages' in error &&
      Array.isArray((error as { messages: unknown }).messages) &&
      (error as { messages: unknown[] }).messages.length > 0
    );
  }
}
